import { deleteItem, saveItem } from "../backtest/labStore";

export const PLUGINS_KEY = "lwt.plugins.v1";
export const PLUGIN_STORE_EVENT = "lwt:plugins-updated";
const MAX_PLUGINS = 40;
const MAX_METRICS = 30;
const SAFE_FORMULAS = new Set(["close_vs_sma", "close_vs_ema", "rsi", "return_pct", "atr_pct", "relative_volume"]);

function read() {
  try {
    const value = JSON.parse(localStorage.getItem(PLUGINS_KEY) || "[]");
    return Array.isArray(value) ? value : [];
  } catch { return []; }
}
function notify() {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(PLUGIN_STORE_EVENT));
}
function safeKey(value) { return /^[a-z0-9][a-z0-9._-]{0,39}$/i.test(String(value || "")); }

/** Validate a content-only plugin. No plugin JavaScript is accepted or evaluated. */
export function validatePlugin(raw) {
  if (!raw || typeof raw !== "object" || raw.manifestVersion !== 1) throw new Error("Use a plugin manifest with manifestVersion: 1.");
  const id = String(raw.id || "").trim();
  const name = String(raw.name || "").trim().slice(0, 50);
  if (!safeKey(id) || !name) throw new Error("Plugin needs a valid id and name.");
  const contributions = raw.contributions && typeof raw.contributions === "object" ? raw.contributions : {};
  const seenMetricIds = new Set();
  const metrics = Array.isArray(contributions.scannerMetrics) ? contributions.scannerMetrics.slice(0, MAX_METRICS).map((metric) => {
    const metricId = String(metric.id || "").trim();
    if (seenMetricIds.has(metricId)) throw new Error(`Scanner metric id “${metricId}” is duplicated.`);
    seenMetricIds.add(metricId);
    if (!safeKey(metricId) || !SAFE_FORMULAS.has(metric.formula)) throw new Error(`Scanner metric “${metricId || "unnamed"}” uses an unsupported formula.`);
    return { id: metricId, label: String(metric.label || metricId).trim().slice(0, 45), group: String(metric.group || "Plugin metrics").trim().slice(0, 30), formula: metric.formula, period: Math.max(2, Math.min(500, Number(metric.period) || 20)) };
  }) : [];
  const labItems = Array.isArray(contributions.labItems) ? contributions.labItems.slice(0, 20).map((item) => {
    const itemId = String(item.id || "").trim();
    if (!safeKey(itemId)) throw new Error("Backtest item needs a valid id.");
    if (!["indicator", "strategy"].includes(item.kind) || typeof item.code !== "string" || !item.code.trim() || item.code.length > 60000) throw new Error(`Backtest item “${itemId}” must contain indicator/strategy Python code under 60,000 characters.`);
    // Imported plugins cannot request arbitrary PyPI packages; that would add a supply-chain
    // execution path when a user later runs the contribution.
    return { id: itemId, kind: item.kind, name: String(item.name || itemId).trim().slice(0, 40), code: item.code, outputs: Array.isArray(item.outputs) ? item.outputs.slice(0, 8).map((x) => String(x).slice(0, 30)) : [], packages: [], params: Array.isArray(item.params) ? item.params.slice(0, 12) : [] };
  }) : [];
  if (!metrics.length && !labItems.length) throw new Error("Plugin has no supported contributions.");
  return { manifestVersion: 1, id, name, version: String(raw.version || "1.0.0").slice(0, 24), description: String(raw.description || "").slice(0, 220), author: String(raw.author || "").slice(0, 60), contributions: { scannerMetrics: metrics, labItems } };
}

export function getPlugins() { return read(); }
export function getEnabledScannerMetrics() {
  return read().filter((plugin) => plugin.enabled).flatMap((plugin) => (plugin.manifest.contributions.scannerMetrics || []).map((metric) => ({ ...metric, id: `plugin_${plugin.manifest.id}_${metric.id}`, pluginId: plugin.manifest.id })));
}
function persist(plugins) { localStorage.setItem(PLUGINS_KEY, JSON.stringify(plugins)); notify(); }
function contributionItemId(pluginId, itemId) { return `plugin:${pluginId}:${itemId}`; }
function syncLabItems(manifest, enabled) {
  (manifest.contributions.labItems || []).forEach((item) => {
    const id = contributionItemId(manifest.id, item.id);
    if (!enabled) { deleteItem(id); return; }
    saveItem({ ...item, id });
  });
}
export function installPlugin(raw) {
  const manifest = validatePlugin(raw);
  const current = read();
  if (!current.some((plugin) => plugin.manifest.id === manifest.id) && current.length >= MAX_PLUGINS) throw new Error(`You can install up to ${MAX_PLUGINS} plugins.`);
  const previous = current.find((plugin) => plugin.manifest.id === manifest.id);
  const enabled = previous ? Boolean(previous.enabled) : false;
  if (previous?.enabled) syncLabItems(previous.manifest, false);
  const next = [...current.filter((plugin) => plugin.manifest.id !== manifest.id), { manifest, enabled }];
  persist(next);
  if (enabled) syncLabItems(manifest, true);
  return manifest;
}
export function setPluginEnabled(id, enabled) {
  const plugins = read();
  const current = plugins.find((plugin) => plugin.manifest.id === id);
  if (!current) return;
  syncLabItems(current.manifest, Boolean(enabled));
  const next = plugins.map((plugin) => plugin.manifest.id === id ? { ...plugin, enabled: Boolean(enabled) } : plugin);
  persist(next);
}
export function removePlugin(id) {
  const plugins = read();
  const plugin = plugins.find((entry) => entry.manifest.id === id);
  if (plugin?.enabled) syncLabItems(plugin.manifest, false);
  persist(plugins.filter((entry) => entry.manifest.id !== id));
}
